import { inject } from '@angular/core';
import { EventLocationInfo } from '@karve.it/interfaces/estimate';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from '@ngrx/store';
import { MutationOptionsAlone } from 'apollo-angular/types';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import {
  EMPTY,
  catchError,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { CancelAndCloseDialogActions } from 'src/app/shared/cancel-and-close-dialog/cancel-and-close-dialog.actions';

import {
  BulkEditCalendarEventGQL,
  CalendarEventLocation,
  CalendarEventLocationInput,
  DocumentReadyToViewGQL,
  JobClosedGQL,
  Job_LocationFragment,
  SingleEditInput,
  UpdateJobGQL,
  UpdateJobMutation,
  UpdateJobMutationVariables,
  ImportToCurrentZoneGQL,
} from '../../generated/graphql.generated';

import { generateMutationContext } from '../core/auth/session';
import { PlusAuthenticationService } from '../core/public-api';
import { CLOSE_JOB_ONGOING_EVENT_STATUSES } from '../global.constants';
import { DetailsHelperService } from '../services/details-helper.service';
import { FreyaHelperService } from '../services/freya-helper.service';
import { FreyaNotificationsService } from '../services/freya-notifications.service';
import { ResponsiveHelperService } from '../services/responsive-helper.service';

import { PrintBolService } from '../shared/bill-of-lading/print-bol.service';
import { UpdateEventLocationsDialogComponent } from '../shared/update-event-locations-dialog/update-event-locations-dialog.component';
import { viewLocationOnGoogleMaps } from '../utilities/locations.util';
import { LoadingState } from '../utilities/state.util';

import { JobActivityActions } from './job-activity/job-activity.actions';
import { CloseJobComponentActions } from './job-create/close-job-component/close-job-component.actions';
import { JobCloseJobComponent } from './job-create/close-job-component/close-job-component.component';
import { JobCreateLocationsActions } from './job-create/job-create-locations/job-create-locations-state.actions';
import { JobFormActionButtonActions } from './job-create/job-form-action-button/job-form-action-button.actions';
import { selectButtonDisabled } from './job-create/job-form-action-button/job-form-action-button.component';
import { JobHeaderActions } from './job-header/job-header.actions';
import { JobStatusDropdownActions } from './job-header/job-status-dropdown/job-status-dropdown.actions';
import { JobAgentActions } from './job-overview/overview-header/job-agent/job-agent.actions';
import { JobLocationsOverviewActions } from './job-overview/overview-locations/overview-locations.actions';
import { OverviewTimelineActions } from './job-overview/overview-timeline/overview-timeline.actions';
import { EventItineraryActions } from './job-overview/overview-timeline/timeline-book-event/event-itinerary/event-itinerary.actions';
import { JobToolSubscriptionActions } from './job-state/job-tool-subscription.actions';
import { JobToolActions } from './job-tool.actions';
import {
  jobToolFeature,
  selectJob,
  selectJobFranchiseId,
  selectJobId,
} from './job-tool.reducer';
import { WorkOrdersActions } from './job-workorders/job-workorders.actions';
import { selectLockDate } from "./job-workorders/job-workorders.selectors";

export const openLocationOnGoogleMaps = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(
        JobLocationsOverviewActions.addressLinkClicked,
        EventItineraryActions.viewLocationOnGoogleMapsButtonClicked,
      ),
      concatLatestFrom(({ locationType }) => store.select(createSelector(
        selectJob,
        (job) => job.locations?.find((l) => l.locationType === locationType),
      ))),
      filter(([ _action, jobLocation ]) => Boolean(jobLocation)),
      map(([ _action, jobLocation ]) => viewLocationOnGoogleMaps(jobLocation.location)),
      tap((url: string) => window.open(url, '_blank')),
    );
}, { functional: true, dispatch: false });

export const notifyJobClosed = createEffect((
  store = inject(Store),
  jobClosedGQL = inject(JobClosedGQL),
  notify = inject(FreyaNotificationsService),
) => {
    return store.select(selectJobId).pipe(
      distinctUntilChanged(),
      switchMap((jobId) => !jobId ? EMPTY : jobClosedGQL.subscribe({ jobId }).pipe(
        tap((res) => notify.success('Job Closed', res.data?.jobClosed?.reason)),
      )),
    );
}, { functional: true, dispatch: false });

export const notifyOfRemoteStageChanges = createEffect((
  actions$ = inject(Actions),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobToolSubscriptionActions.remoteJobUpdated),
      filter((action) => Boolean(action.input?.stage)),
      map((action) => {

        const name = action.userName || 'Someone';
        const stage = action.input.stage || action.output?.job?.stage;

        const summary = `${ name } promoted job to ${ stage }`;

        if (!action.input.promoteReason) {
          return { summary };
        }

        return {
          summary,
          details: `Reason for update: ${action.input.promoteReason}`,
        };
      }),
      tap(({ summary, details }: { summary: string; details?: string; }) => notify.success(summary, details)),
    );
  }, { functional: true, dispatch: false });

export const showUpdateLocationsPrompt = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  dialogService = inject(DialogService),
  freyaHelper = inject(FreyaHelperService),
  responsiveHelper = inject(ResponsiveHelperService),
  bulkEditCalendarEventGQL = inject(BulkEditCalendarEventGQL),
) => {
    return actions$.pipe(
      ofType(
        JobCreateLocationsActions.updateLocationsSuccess,
        JobLocationsOverviewActions.addJobLocationSuccess,
        JobFormActionButtonActions.updateJobSuccess,
        JobToolActions.saveChangesSuccess,
      ),
      filter((action) => {
        // These actions always involve location changes
        if (action.type === JobCreateLocationsActions.updateLocationsSuccess.type ||
            action.type === JobLocationsOverviewActions.addJobLocationSuccess.type) {
          return true;
        }
        // For general save actions, check the flag
        return 'locationsChanged' in action && action.locationsChanged;
      }),
      concatLatestFrom(() => [
        store.select(selectJob),
        store.select(selectLockDate),
      ]),
      map(([ _action, job, lockDate ]) => {

        let hasLockedEvents = false;

        const jobLocationsLookUp = job.locations.reduce((acc, location) => {
          if (location.locationType) {
            acc[location.locationType] = location;
          }
          return acc;
        }, {});

        // Confirmation dialog data
        const eventLocations: EventLocationInfo[] = [];

        // Mutation data
        const edits: SingleEditInput[] = [];

        for (const event of job.events) {

          // Confirmation dialog data
          const changedLocations: EventLocationInfo['locations'] = [];

          // Mutation data
          const addLocations: CalendarEventLocationInput[] = [];
          const removeLocations: string[] = [];

          for (const location of event.locations) {

            const jobLocation: Job_LocationFragment = jobLocationsLookUp[location.type];

            if (!jobLocation) continue;
            if (jobLocation.location.id === location.location.id) continue;

            changedLocations.push({
              currentLocation: location as CalendarEventLocation,
              newLocation: {
                address: jobLocation.location.addressLineOne,
                locationId: jobLocation.location.id,
                locationType: location.type,
              }
            });

            addLocations.push({
              locationId: jobLocation.location.id,
              type: location.type,
              order: location.order,
              estimatedTimeAtLocation: location.estimatedTimeAtLocation,
            });

            removeLocations.push(location.id);
          }

          if (changedLocations.length > 0) {
            eventLocations.push({
              event,
              locations: changedLocations,
            });
          }

          if (addLocations.length || removeLocations.length) {
            edits.push({
              id: event.id,
              edit: {
                setLocations: {
                  addLocations,
                  removeLocations
                },
              },
            });

            if (event.end && lockDate > event.end) {
              hasLockedEvents = true;
            }
          }
        }

        return {
          eventLocations,
          edits,
          jobId: job.id,
          hasLockedEvents,
        };
      }),
      filter(({ eventLocations }) => eventLocations.length > 0),
      exhaustMap(({ eventLocations, edits, jobId }) => dialogService.open(UpdateEventLocationsDialogComponent, {
        header: 'Update Event Locations?',
        contentStyle: freyaHelper.getDialogContentStyle('1.5rem'),
        width: responsiveHelper.dialogWidth,
        closable: false,
        data: { eventLocations },
      }).onClose.pipe(
        filter((confirmed) => confirmed),
        mergeMap(() => bulkEditCalendarEventGQL.mutate({ edits }).pipe(
          map((res) => JobToolActions.updateEventLocationsSuccess({
            updatedEvent: res.data.bulkEditCalendarEvent.events[0],
            jobId,
          })),
          catchError((error) => of(JobToolActions.updateEventLocationsError({ error }))),
        )),
      )),
    );
  }, { functional: true, dispatch: true });

export const importJobToCurrentZone = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  importToCurrentZoneGQL = inject(ImportToCurrentZoneGQL),
) => {
    return actions$.pipe(
      ofType(JobToolActions.importJobButtonClicked),
      concatLatestFrom(() => [
        store.select(selectJobId),
        store.select(selectJobFranchiseId),
      ]),
      filter(([ _, jobId, jobFranchiseId ]) => Boolean(jobId && jobFranchiseId)),
      exhaustMap(([ _, jobId, jobFranchiseId ]) => importToCurrentZoneGQL.mutate(
        { jobIds: [jobId] },
        { context: generateMutationContext({ zone: jobFranchiseId }) }
      ).pipe(
        map((res) => {

          const updatedJob = res.data?.importToCurrentZone?.jobs?.[0];

          if (!updatedJob) {
            throw new Error('Import returned no jobs');
          }

          return { updatedJob };
        }),
        map((payload) => JobToolActions.importJobToCurrentZoneSuccess(payload)),
        catchError((error) => of(JobToolActions.importJobToCurrentZoneError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const routeCloseJobAction = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(
        JobHeaderActions.closeJobMenuItemClicked,
        JobStatusDropdownActions.closeJobMenuItemClicked,
        JobFormActionButtonActions.createAndCloseJobButtonClicked,
        JobFormActionButtonActions.createAndCloseJobShortcutEntered,
        JobFormActionButtonActions.updateAndCloseJobButtonClicked,
      ),
      concatLatestFrom(() => [
        store.select(selectButtonDisabled),
        store.select(createSelector(
          selectJob,
          (job) => (job?.events || []).filter((e) => CLOSE_JOB_ONGOING_EVENT_STATUSES.includes(e.status)).map((e) => e.id),
        )),
        store.select(selectJobId),
      ]),
      filter(([ action, buttonDisabled ]) => {

        if (
          action.type === JobHeaderActions.closeJobMenuItemClicked.type ||
          action.type === JobStatusDropdownActions.closeJobMenuItemClicked.type
        ) {
          return true;
        }
        return !buttonDisabled;
      }),
      map(([ promptedBy, _buttonDisabled, pendingEventIds, jobId ]) => {

        if (pendingEventIds.length > 0) {
          return CancelAndCloseDialogActions.openCancelAndCloseDialogTriggered({
            jobId,
            trigger: 'close',
            eventIds: pendingEventIds,
          });
        }

        return CloseJobComponentActions.dialogPrompted({ promptedBy });
      }),
    );
  },
  { functional: true, dispatch: true },
);

export const openCloseJobDialog = createEffect((
  actions$ = inject(Actions),
  dialogService = inject(DialogService),
) => {
    return actions$.pipe(
      ofType(CloseJobComponentActions.dialogPrompted),
      map(({ promptedBy }) => {
        if (promptedBy.type === JobFormActionButtonActions.updateAndCloseJobButtonClicked.type) {
          return 'Update Job and Close It Right Away';
        }
        return 'Close Job';
      }),
      tap((header: string) => dialogService.open(JobCloseJobComponent, {
        header,
        width: '20rem',
      })),
    );
  },
  { functional: true, dispatch: false },
);

export const closeJob = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    updateJobGQL = inject(UpdateJobGQL),
) => {
    return actions$.pipe(
      ofType(
        CloseJobComponentActions.closeJobButtonClicked,
        JobStatusDropdownActions.closureReasonMenuItemSelected,
      ),
      mergeMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(selectJobId),
          store.select(jobToolFeature.selectClosedReason),
          store.select(selectJobFranchiseId),
          store.select(jobToolFeature.selectClosedReasonsDialogPromptedBy),
        ]),
        filter(([ action, _jobId, _closedReason, _jobFranchiseId, promptedBy ]) => {

          if (action.type !== CloseJobComponentActions.closeJobButtonClicked.type) {
            return true;
          }

          return promptedBy.type === JobHeaderActions.closeJobMenuItemClicked.type;
        }),
        map(([ _action, jobId, closedReason, jobFranchiseId ]): {
          vars: UpdateJobMutationVariables,
          opts: MutationOptionsAlone<UpdateJobMutation, UpdateJobMutationVariables>,
        } => ({
          vars: { updateJobs: {
            jobId,
            closeJob: true,
            closedReason,
          }},
          opts: {
            context: generateMutationContext({
              zone: jobFranchiseId,
            }),
          },
        })),
        exhaustMap(({ vars, opts }) => updateJobGQL.mutate(vars, opts).pipe(
          tap((res) => {

            const { isSuccess } = res.data.updateJobs;

            if (!isSuccess) {
              throw new Error('Failed to update job');
            }
          }),
          map((res) => CloseJobComponentActions.closeJobSuccess({ updatedJob: res.data.updateJobs.jobs[0] })),
        )),
        catchError((error) => of(CloseJobComponentActions.closeJobError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const openJob = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    updateJobGQL = inject(UpdateJobGQL),
) => {
    return actions$.pipe(
      ofType(
        JobHeaderActions.openJobMenuItemClicked,
        JobStatusDropdownActions.openJobMenuItemClicked,
      ),
      mergeMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(selectJobId),
          store.select(selectJobFranchiseId),
        ]),
        map(([ _action, jobId, jobFranchiseId ]): {
          vars: UpdateJobMutationVariables,
          opts: MutationOptionsAlone<UpdateJobMutation, UpdateJobMutationVariables> } => ({
          vars: { updateJobs: {
            jobId,
            closeJob: false,
          }},
          opts: {
            context: generateMutationContext({
              zone: jobFranchiseId,
            }),
          },
        })),
        exhaustMap(({ vars, opts }) => updateJobGQL.mutate(vars, opts).pipe(
          tap((res) => {

            const { isSuccess } = res.data.updateJobs;

            if (!isSuccess) {
              throw new Error('Failed to open job');
            }
          }),
          map((res) => JobHeaderActions.openJobSuccess({ updatedJob: res.data.updateJobs.jobs[0] })),
        )),
        catchError((error) => of(JobHeaderActions.openJobError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const openEventDetails = createEffect((
  actions$ = inject(Actions),
  detailsHelper = inject(DetailsHelperService),
) => {
    return actions$.pipe(
      ofType(
        WorkOrdersActions.eventDetailsClicked,
        OverviewTimelineActions.eventDetailsClicked,
      ),
      tap(({ eventId }) => detailsHelper.open('calendar-event', { id: eventId })),
    );
  }, { functional: true, dispatch: false });

export const openUserInSidePanel = createEffect((
  actions$ = inject(Actions),
  detailsHelper = inject(DetailsHelperService),
) => {
  return actions$.pipe(
    ofType(
      JobAgentActions.agentNameClicked,
      JobActivityActions.authorNameClicked,
    ),
    tap(({ userId }) => detailsHelper.open('users', { id: userId })),
  );
}, { functional: true, dispatch: false });

export const setJobMutating = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(
        JobFormActionButtonActions.updateJobButtonClicked,
      ),
      concatLatestFrom(() => store.select(selectJobId)),
      map(([ _action, jobId ]) => JobToolActions.jobLoadingStateChanged({
        jobId,
        loadingState: LoadingState.MUTATING,
      })),
    );
  }, { functional: true, dispatch: true });

export const setJobMutated = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(
        JobFormActionButtonActions.updateJobSuccess,
      ),
      concatLatestFrom(() => store.select(selectJobId)),
      map(([ _action, jobId ]) => JobToolActions.jobLoadingStateChanged({
        jobId,
        loadingState: LoadingState.MUTATED,
      })),
    );
  }, { functional: true, dispatch: true });

export const setJobError = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(
        JobFormActionButtonActions.buildUpdateJobVariablesError,
        JobFormActionButtonActions.updateJobError,
      ),
      concatLatestFrom(() => store.select(selectJobId)),
      map(([ { error }, jobId ]) => JobToolActions.jobLoadingStateChanged({
        jobId,
        loadingState: { error },
      })),
    );
  }, { functional: true, dispatch: true });

export const confirmCancelEvent = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  confirmationService = inject(ConfirmationService),
) => {
    return actions$.pipe(
      ofType(
        OverviewTimelineActions.cancelEventMenuItemSelected,
      ),
      tap(() => confirmationService.confirm({
        header: 'Cancel Event?',
        message: `Cancelling this event will remove it from the schedule and remove any associated charges and discounts`,
        acceptLabel: 'Cancel this Event',
        acceptIcon: 'pi pi-ban',
        rejectLabel: `Don't Cancel this Event`,
        rejectIcon: 'pi pi-times',
        accept: () => store.dispatch(OverviewTimelineActions.confirmCancelEventButtonClicked()),
      })),
    );
}, { functional: true, dispatch: false });



export const subscribeToDocumentReadyToViewEvents = createEffect((
  store = inject(Store),
  documentReadyToViewGQL = inject(DocumentReadyToViewGQL),
  plusAuth = inject(PlusAuthenticationService),
  detailsHelper = inject(DetailsHelperService),
  localNotify = inject(FreyaNotificationsService),
) => {
    return store.select(selectJobId).pipe(
      distinctUntilChanged(),
      switchMap((jobId) => !jobId ? EMPTY : documentReadyToViewGQL.subscribe({ jobId }).pipe(
        map((res) => res.data?.documentReadyToView),
        filter((doc) => doc.userId === plusAuth.user.id),
        tap((doc) => {

          const { artifact, templateType } = doc;

          if (templateType === 'invoice') {
            detailsHelper.pushUpdate({
              action: 'update',
              id: artifact.id,
              type: 'Invoice',
            });

            localNotify.success('Invoice generated');

          }

          if (templateType === 'receipt' && doc.trigger === 'automatic') {
            localNotify.success('Receipt generated');
          }

          detailsHelper.pushUpdate({
            action: 'update',
            id: artifact.id,
            type: 'Artifacts',
          });

        }),
        map((doc) => JobToolActions.documentReadyToView(doc)),
      )),
    );
  }, { functional: true, dispatch: true });


// The QuickJS calculator effects — fetchScript, forceReloadScript, runCalculations and
// runCalculatorButtonAction — live in `job.effects.ts` only. Do not re-add them here.
// The `/jobs/:jobId` route registers BOTH this module and `job.effects`, so a copy in each
// file makes every calculator action run twice, and the losing run overwrites the winner's
// output. A stale duplicate of `runCalculations` here (missing `sessionZone`) is what made
// the interstate sections vanish on the first keystroke in an input.

export const printBol = createEffect((
  actions$ = inject(Actions),
  printBolService = inject(PrintBolService),
) => {
    return actions$.pipe(
      ofType(JobToolActions.printBolRequested),
      tap(({ eventId, jobId }) => printBolService.printEvents([{ eventId, jobId }])),
    );
}, { functional: true, dispatch: false });

export const printAllBols = createEffect((
  actions$ = inject(Actions),
  printBolService = inject(PrintBolService),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobToolActions.printAllBolsRequested),
      concatLatestFrom(() => store.select(selectJob)),
      tap(([ { jobId }, job ]) => {
        if (job && job.events && job.events.length) {
            const events = job.events.map(e => ({ eventId: e.id, jobId }));
            printBolService.printEvents(events);
        }
      }),
    );
}, { functional: true, dispatch: false });

